// Recovery panel — pure logic module.
//
// Tracer 14 (#657). The Stimulus controller `recovery_panel_controller.js`
// owns the DOM lifecycle (open/close, button wiring, listening to the sync
// indicator). All business logic — header colour mapping, op formatting,
// export-blob construction, double-confirm timing for the destructive
// "discard" action, and the 30-second persistent-error banner — lives here so
// it can be unit-tested in isolation.
//
// PRD reference: docs/plans/2026-05-06-cda-results-rebuild-prd.md §7.9
// (decyzja #97 rev — uproszczony recovery panel without per-run conflict
// resolution and without lock status sections).

import type { Op, Snapshot } from "../offline_capture_store/types";

// ---------------------------------------------------------------------------
// Header status mapping
// ---------------------------------------------------------------------------

/**
 * Header colour shown at the top of the recovery panel. Mirrors the sync
 * indicator dot semantics so a user can glance at either and form the same
 * mental model.
 *
 *   green   — fully synced, no pending ops.
 *   yellow  — sync in flight.
 *   orange  — pending ops, no sync attempted yet.
 *   red     — last sync attempt errored / conflicted.
 */
export type PanelHeaderState = "green" | "yellow" | "orange" | "red";

export const PANEL_HEADER_STATES = {
  GREEN:  "green",
  YELLOW: "yellow",
  ORANGE: "orange",
  RED:    "red",
} as const satisfies Record<string, PanelHeaderState>;

/**
 * The five SyncEngine event names we observe to drive the panel header. We
 * deliberately accept arbitrary strings as well so callers do not have to
 * narrow the type — unknown events simply fall through to the pending-count
 * heuristic.
 */
export type SyncEventName =
  | "sync:syncing"
  | "sync:success"
  | "sync:pending"
  | "sync:error"
  | "sync:conflict"
  | (string & {});

export interface HeaderStateInput {
  /** Most recent sync event name observed, or null before any event fires. */
  lastEvent: SyncEventName | null;
  /** Count of WAL ops still in `pending` status. */
  pendingCount: number;
  /**
   * Count of dead-lettered (`failed`) ops (#883) — permanently rejected
   * (404/422), retired from the retry loop, awaiting manual recovery.
   */
  failedCount?: number;
}

/**
 * Map (last sync event, pending op count) → header colour. The error / conflict
 * branches win unconditionally so a stale pending count does not mask a fresh
 * failure. Otherwise active syncing wins, then pending ops, then dead-lettered
 * ops (#883 — amber: local values still await manual recovery), then idle/green.
 */
export function headerStateForSync(input: HeaderStateInput): PanelHeaderState {
  const { lastEvent, pendingCount, failedCount = 0 } = input;
  if (lastEvent === "sync:error" || lastEvent === "sync:conflict") {
    return PANEL_HEADER_STATES.RED;
  }
  if (lastEvent === "sync:syncing") {
    return PANEL_HEADER_STATES.YELLOW;
  }
  if (pendingCount > 0 || failedCount > 0) {
    return PANEL_HEADER_STATES.ORANGE;
  }
  return PANEL_HEADER_STATES.GREEN;
}

// ---------------------------------------------------------------------------
// Op formatting
// ---------------------------------------------------------------------------

interface FormatOpOptions {
  /** Browser-locale string (e.g. "pl-PL"). Defaults to user's runtime locale. */
  locale?: string;
  /** IANA timezone (e.g. "UTC"). Defaults to user's runtime tz. */
  timeZone?: string;
}

/**
 * Render a single op as a human-readable line for the "Lokalne zmiany" /
 * "Sync log" sections. Format roughly:
 *
 *   "Run #5: cda → 0.221 [pending] 14:23"
 *
 * The format is deliberately stable but informational only — callers that
 * need machine-parseable data should still use the underlying op object.
 */
export function formatOp(op: Op, options: FormatOpOptions = {}): string {
  const time = new Date(op.createdAt).toLocaleTimeString(options.locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: options.timeZone,
  });

  const subject = describeOpSubject(op);
  const detail = describeOpDetail(op);
  const status = `[${op.status}]`;

  // We always include the opType when we cannot derive a richer subject so
  // the log stays diagnostic for unknown opTypes.
  const head = subject || op.opType;
  const body = detail ? `${head}: ${detail}` : head;
  return `${body} ${status} ${time}`.trim();
}

// ---------------------------------------------------------------------------
// List view models
// ---------------------------------------------------------------------------

export type Translate = (key: string, fallback: string) => string;

export interface OpsRowViewModel {
  clientOpId: string;
  status: string;
  text: string;
  failed: boolean;
}

export interface OpsListViewModel {
  empty: boolean;
  rows: OpsRowViewModel[];
}

export function buildOpsViewModel(ops: Op[]): OpsListViewModel {
  const rows = ops
    .filter((op) => op.status === "pending" || op.status === "syncing" || op.status === "failed")
    .map((op) => ({
      clientOpId: String(op.clientOpId ?? ""),
      status: String(op.status ?? ""),
      text: formatOp(op),
      failed: op.status === "failed",
    }));

  return { empty: rows.length === 0, rows };
}

export interface SyncLogRowViewModel {
  status: string;
  text: string;
}

export interface SyncLogViewModel {
  empty: boolean;
  rows: SyncLogRowViewModel[];
}

export function buildSyncLogViewModel(
  ops: Op[],
  options: { filter?: string; limit?: number } = {},
): SyncLogViewModel {
  const filter = options.filter ?? "";
  const limit = options.limit ?? 50;
  const filtered = filter ? ops.filter((op) => op.status === filter) : ops;
  const rows = filtered.slice(-limit).reverse().map((op) => ({
    status: String(op.status ?? ""),
    text: formatOp(op),
  }));

  return { empty: rows.length === 0, rows };
}

export interface SnapshotRowViewModel {
  id: string;
  label: string;
}

export interface SnapshotsViewModel {
  empty: boolean;
  rows: SnapshotRowViewModel[];
}

export function buildSnapshotsViewModel(
  snapshots: Snapshot[],
  options: { formatTakenAt?: (takenAt: number) => string } = {},
): SnapshotsViewModel {
  const formatTakenAt = options.formatTakenAt ?? ((takenAt) => new Date(takenAt).toLocaleString());
  const rows = snapshots.map((snapshot) => ({
    id: String(snapshot.id ?? ""),
    label: formatTakenAt(snapshot.takenAt),
  }));

  return { empty: rows.length === 0, rows };
}

export interface LocalDataRowViewModel {
  runId: string;
  field: string;
  cells: string[];
}

export interface LocalDataViewModel {
  empty: boolean;
  rows: LocalDataRowViewModel[];
}

export function buildLocalDataViewModel(
  ops: Op[],
  options: { translate?: Translate } = {},
): LocalDataViewModel {
  const translate = options.translate ?? ((_key, fallback) => fallback);
  const byKey = new Map<string, {
    runId: string;
    runNumber: unknown;
    field: string;
    value: unknown;
    status: unknown;
  }>();

  ops.forEach((op) => {
    const payload = op.payload;
    if (!payload || typeof payload !== "object") return;

    const data = payload as Record<string, unknown>;
    const field = data.field;
    if (typeof field !== "string" || field.length === 0) return;

    byKey.set(`${data.runId}::${field}`, {
      runId: String(data.runId ?? ""),
      runNumber: data.runNumber ?? null,
      field,
      value: data.value,
      status: op.status,
    });
  });

  const rows = Array.from(byKey.values()).map((row): LocalDataRowViewModel => {
    const runLabel = row.runNumber != null && row.runNumber !== ""
      ? `#${row.runNumber}`
      : (row.runId === "new" ? translate("local_data_new_run", "nowy") : row.runId);

    return {
      runId: row.runId,
      field: row.field,
      cells: [
        runLabel,
        translate(`field_${row.field}`, row.field),
        formatLocalValue(row.value),
        translate(`filter_${row.status}`, String(row.status ?? "")),
      ],
    };
  });

  return { empty: rows.length === 0, rows };
}

export function formatLocalValue(value: unknown): string {
  if (value === true) return "✓";
  if (value === false) return "—";
  if (value === null || value === undefined) return "";
  return String(value);
}

function describeOpSubject(op: Op): string | null {
  const payload = op.payload;
  if (payload && typeof payload === "object" && "runNumber" in payload) {
    const runNumber = (payload as { runNumber: unknown }).runNumber;
    if (typeof runNumber === "number" || typeof runNumber === "string") {
      return `Run #${runNumber}`;
    }
  }
  return null;
}

function describeOpDetail(op: Op): string | null {
  const payload = op.payload;
  if (op.opType === "update_run_field" && payload && typeof payload === "object") {
    const { field, value } = payload as { field?: unknown; value?: unknown };
    if (typeof field === "string") {
      const formattedValue = value === undefined || value === null ? "" : String(value);
      return formattedValue ? `${field} → ${formattedValue}` : field;
    }
  }
  if (op.opType !== "update_run_field") {
    // Surface the opType as the detail when no friendly mapping exists, but
    // only if the subject already gave us context. Otherwise the subject
    // fallback above already includes the opType.
    return op.opType;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Export blob (Eksportuj lokalne dane)
// ---------------------------------------------------------------------------

export interface ExportBlobInput {
  sessionId: string;
  /** Latest persisted state from `OfflineStore.getSession`. */
  session: unknown;
  ops: Op[];
  snapshots: Snapshot[];
}

/**
 * Build a downloadable JSON Blob containing everything the recovery panel
 * keeps in IndexedDB for `sessionId`. Used by the "Eksportuj lokalne dane"
 * action — the controller wraps the blob in an `<a>` + `URL.createObjectURL`
 * to trigger a download.
 */
export function buildExportBlob(input: ExportBlobInput): Blob {
  const payload = {
    sessionId: input.sessionId,
    session: input.session,
    ops: input.ops,
    snapshots: input.snapshots,
    exportedAt: Date.now(),
  };
  return new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
}

/**
 * File name used by the "Eksportuj lokalne dane" download. ISO timestamp
 * keeps the file sortable in Finder / Explorer.
 */
export function exportFileName(sessionId: string, now: Date = new Date()): string {
  const stamp = now.toISOString().replace(/[:.]/g, "-");
  return `recovery-${sessionId}-${stamp}.json`;
}

// ---------------------------------------------------------------------------
// Double-confirm timing for "Odrzuć lokalne zmiany"
// ---------------------------------------------------------------------------

/**
 * Window during which a second click on the destructive "Odrzuć lokalne
 * zmiany" button executes; outside the window we re-arm. PRD calls for 5s.
 */
export const DISCARD_CONFIRM_WINDOW_MS = 5_000;

interface DiscardConfirmOptions {
  /** Override the timeout — primarily for tests. */
  windowMs?: number;
  /** Called when the armed state expires without a second click. */
  onDisarm?: () => void;
}

/**
 * Result of a single `request()` invocation.
 *
 *   armed=true,  executed=false  — first click; UI should show the warning.
 *   armed=false, executed=true   — second click within the window; perform.
 *   armed=true,  executed=false  — second click after expiry; treat as fresh
 *                                  arming and re-show the warning.
 */
export interface DiscardConfirmResult {
  armed: boolean;
  executed: boolean;
}

export class DiscardConfirmController {
  private armedAt: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly windowMs: number;
  private readonly onDisarm: (() => void) | null;

  constructor(options: DiscardConfirmOptions = {}) {
    this.windowMs = options.windowMs ?? DISCARD_CONFIRM_WINDOW_MS;
    this.onDisarm = options.onDisarm ?? null;
  }

  get armed(): boolean {
    return this.armedAt !== null;
  }

  request(): DiscardConfirmResult {
    if (this.armedAt === null) {
      this.arm();
      return { armed: true, executed: false };
    }

    const elapsed = Date.now() - this.armedAt;
    if (elapsed <= this.windowMs) {
      this.clear();
      return { armed: false, executed: true };
    }

    // Expired but the timer somehow has not fired yet (shouldn't happen with
    // the real clock; defensive). Treat as fresh arming.
    this.arm();
    return { armed: true, executed: false };
  }

  reset(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.armedAt = null;
    this.timer = null;
  }

  private arm(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.armedAt = Date.now();
    this.timer = setTimeout(() => {
      this.armedAt = null;
      this.timer = null;
      this.onDisarm?.();
    }, this.windowMs);
  }

  private clear(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.armedAt = null;
    this.timer = null;
  }
}

// ---------------------------------------------------------------------------
// 30-second persistent-error banner timer
// ---------------------------------------------------------------------------

/**
 * Threshold (ms) after which a continuously-failing sync surfaces the
 * "Sync error — kliknij aby otworzyć Recovery" banner. PRD calls for 30s.
 */
export const ERROR_BANNER_THRESHOLD_MS = 30_000;

interface ErrorBannerOptions {
  thresholdMs?: number;
  onShow: () => void;
  onHide?: () => void;
}

/**
 * Tracks whether the SyncEngine has been in an error state long enough to
 * justify surfacing the recovery banner. Behaviour:
 *
 *  - First sync:error arms a timer for `thresholdMs`.
 *  - Subsequent sync:error events are *not* stacked — the original timer
 *    keeps running so a quickly-retrying engine still earns the banner at
 *    the original deadline.
 *  - Any non-error event (success / pending / syncing) cancels the pending
 *    timer; if the banner had already fired, `onHide` is invoked.
 *
 * The class is environment-agnostic (no DOM access) so it works in vitest's
 * default Node environment.
 */
export class ErrorBannerTimer {
  private readonly thresholdMs: number;
  private readonly onShow: () => void;
  private readonly onHide: (() => void) | null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private shown = false;

  constructor(options: ErrorBannerOptions) {
    this.thresholdMs = options.thresholdMs ?? ERROR_BANNER_THRESHOLD_MS;
    this.onShow = options.onShow;
    this.onHide = options.onHide ?? null;
  }

  handleSyncEvent(eventName: string): void {
    if (eventName === "sync:error" || eventName === "sync:conflict") {
      // Do not stack timers — the banner deadline is anchored to the FIRST
      // failure so a flapping engine still earns the banner on schedule.
      if (this.timer === null && !this.shown) {
        this.timer = setTimeout(() => {
          this.timer = null;
          this.shown = true;
          this.onShow();
        }, this.thresholdMs);
      }
      return;
    }

    // Any other event clears the alarm. We invoke `onHide` for both
    // already-shown banners AND still-pending timers — callers typically
    // bind it to "ensure banner element is removed from the DOM" which is
    // idempotent and harmless when nothing was visible.
    const wasArmed = this.timer !== null || this.shown;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.shown = false;
    if (wasArmed) this.onHide?.();
  }

  reset(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.shown = false;
  }
}
